import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from '../../utils/axiosConfig';
import { handleApiError } from '../../utils/apiErrorHandler';

interface Club {
  _id: string;
  name: string;
}

interface ExtensionHistory {
  extendedBy: {
    _id: string;
    name: string;
  };
  extendedAt: string;
  newDeadline: string;
  extensionDays: number;
  reason: string;
}

interface RegistrationExtension {
  _id: string;
  competition: string;
  club: {
    _id: string;
    name: string;
  };
  extendedDeadline: string;
  originalDeadline: string;
  createdBy: {
    _id: string;
    name: string;
  };
  isActive: boolean;
  reason: string;
  createdAt: string;
  extensions?: ExtensionHistory[];
}

interface RegistrationExtensionsPanelProps {
  competitionId: string;
}

const RegistrationExtensionsPanel: React.FC<RegistrationExtensionsPanelProps> = ({ competitionId }) => {
  const [extensions, setExtensions] = useState<RegistrationExtension[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [selectedExtensionId, setSelectedExtensionId] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Form state
  const [selectedClub, setSelectedClub] = useState('');
  const [extensionDays, setExtensionDays] = useState(1);
  const [reason, setReason] = useState('');

  // Extend form state
  const [extendDays, setExtendDays] = useState(1);
  const [extendReason, setExtendReason] = useState('');

  useEffect(() => {
    fetchExtensions();
    fetchClubs();
  }, [competitionId]);

  const fetchExtensions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/registration-extensions/${competitionId}`);
      setExtensions(response.data);
    } catch (error) {
      handleApiError(error, 'Fehler beim Laden der Meldungsverlängerungen');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await axios.get(`/api/registration-extensions/clubs/${competitionId}`);
      setClubs(response.data);
    } catch (error) {
      handleApiError(error, 'Fehler beim Laden der Vereine');
    }
  };

  const handleCreateExtension = async () => {
    if (!selectedClub || !extensionDays) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/registration-extensions', {
        competitionId,
        clubId: selectedClub,
        extensionDays,
        reason
      });

      toast.success('Meldungsverlängerung erfolgreich erstellt');
      setDialogOpen(false);
      resetForm();
      fetchExtensions();
    } catch (error) {
      handleApiError(error, 'Fehler beim Erstellen der Meldungsverlängerung');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendExtension = async () => {
    if (!selectedExtensionId || !extendDays) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      setLoading(true);
      await axios.put(`/api/registration-extensions/${selectedExtensionId}/extend`, {
        extensionDays: extendDays,
        reason: extendReason
      });

      toast.success('Meldungsverlängerung erfolgreich erweitert');
      setExtendDialogOpen(false);
      resetExtendForm();
      fetchExtensions();
    } catch (error) {
      handleApiError(error, 'Fehler beim Erweitern der Meldungsverlängerung');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExtension = async (extensionId: string) => {
    if (!window.confirm('Möchten Sie diese Meldungsverlängerung wirklich deaktivieren?')) {
      return;
    }

    try {
      await axios.delete(`/api/registration-extensions/${extensionId}`);
      toast.success('Meldungsverlängerung deaktiviert');
      fetchExtensions();
    } catch (error) {
      handleApiError(error, 'Fehler beim Deaktivieren der Meldungsverlängerung');
    }
  };

  const handleExtendClick = (extensionId: string, currentReason: string) => {
    setSelectedExtensionId(extensionId);
    setExtendReason(currentReason); // Pre-fill with current reason
    setExtendDialogOpen(true);
  };

  const toggleRowExpansion = (extensionId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(extensionId)) {
      newExpanded.delete(extensionId);
    } else {
      newExpanded.add(extensionId);
    }
    setExpandedRows(newExpanded);
  };

  const resetForm = () => {
    setSelectedClub('');
    setExtensionDays(1);
    setReason('');
  };

  const resetExtendForm = () => {
    setSelectedExtensionId('');
    setExtendDays(1);
    setExtendReason('');
  };

  const isExpired = (deadline: string) => {
    return new Date(deadline) < new Date();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getStatusChip = (extension: RegistrationExtension) => {
    if (!extension.isActive) {
      return <Chip label="Deaktiviert" color="default" size="small" />;
    }
    if (isExpired(extension.extendedDeadline)) {
      return <Chip label="Abgelaufen" color="error" size="small" />;
    }
    return <Chip label="Aktiv" color="success" size="small" />;
  };

  const canExtend = (extension: RegistrationExtension) => {
    return !extension.isActive || isExpired(extension.extendedDeadline);
  };

  const canDelete = (extension: RegistrationExtension) => {
    return extension.isActive && !isExpired(extension.extendedDeadline);
  };

  const activeExtensions = extensions.filter(ext => ext.isActive && !isExpired(ext.extendedDeadline));
  const inactiveExtensions = extensions.filter(ext => !ext.isActive || isExpired(ext.extendedDeadline));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Meldungsverlängerungen
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Verlängerung hinzufügen
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {activeExtensions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aktive Verlängerungen
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="text.secondary">
                {inactiveExtensions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inaktive Verlängerungen
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Extensions Alert */}
      {activeExtensions.length > 0 && (
        <Alert 
          severity="info" 
          icon={<TimeIcon />}
          sx={{ mb: 2 }}
        >
          Es gibt {activeExtensions.length} aktive Meldungsverlängerung(en). 
          Diese Vereine können weiterhin Meldungen abgeben.
        </Alert>
      )}

      {/* Extensions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="30px"></TableCell>
              <TableCell>Verein</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ursprünglicher Meldeschluss</TableCell>
              <TableCell>Erweiterter Meldeschluss</TableCell>
              <TableCell>Grund</TableCell>
              <TableCell>Erstellt von</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {extensions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Keine Meldungsverlängerungen vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              extensions.map((extension) => (
                <React.Fragment key={extension._id}>
                  <TableRow>
                    <TableCell>
                      {extension.extensions && extension.extensions.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(extension._id)}
                        >
                          {expandedRows.has(extension._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>{extension.club.name}</TableCell>
                    <TableCell>{getStatusChip(extension)}</TableCell>
                    <TableCell>{formatDateTime(extension.originalDeadline)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {formatDateTime(extension.extendedDeadline)}
                        {isExpired(extension.extendedDeadline) && extension.isActive && (
                          <WarningIcon color="error" sx={{ ml: 1 }} fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{extension.reason || '-'}</TableCell>
                    <TableCell>{extension.createdBy.name}</TableCell>
                    <TableCell>{formatDateTime(extension.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {canDelete(extension) && (
                          <Tooltip title="Deaktivieren">
                            <IconButton
                              onClick={() => handleDeleteExtension(extension._id)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canExtend(extension) && (
                          <Tooltip title="Verlängern/Reaktivieren">
                            <IconButton
                              onClick={() => handleExtendClick(extension._id, extension.reason)}
                              color="primary"
                              size="small"
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  
                  {/* Extension History Row */}
                  {extension.extensions && extension.extensions.length > 0 && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={expandedRows.has(extension._id)} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                              <HistoryIcon sx={{ mr: 1 }} />
                              Verlängerungshistorie
                            </Typography>
                            <List dense>
                              {extension.extensions.map((ext, index) => (
                                <ListItem key={index}>
                                  <ListItemText
                                    primary={`Verlängert um ${ext.extensionDays} Tag(e) bis ${formatDateTime(ext.newDeadline)}`}
                                    secondary={`Von ${ext.extendedBy?.name || 'Unbekannt'} am ${formatDateTime(ext.extendedAt)}${ext.reason ? ` - Grund: ${ext.reason}` : ''}`}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Extension Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Meldungsverlängerung erstellen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Verein</InputLabel>
              <Select
                value={selectedClub}
                label="Verein"
                onChange={(e) => setSelectedClub(e.target.value)}
              >
                {clubs.map((club) => (
                  <MenuItem key={club._id} value={club._id}>
                    {club.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Verlängerung (Tage)"
              type="number"
              value={extensionDays}
              onChange={(e) => setExtensionDays(parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: 30 }}
              helperText="Anzahl der Tage, um die der Meldeschluss verlängert wird (1-30)"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Grund (optional)"
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund für die Meldungsverlängerung..."
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Die Verlängerung wird ab heute für {extensionDays} Tag(e) gewährt und läuft um 23:59 Uhr des letzten Tages ab.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleCreateExtension} 
            variant="contained"
            disabled={loading || !selectedClub}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Extension Dialog */}
      <Dialog open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Meldungsverlängerung erweitern</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Zusätzliche Verlängerung (Tage)"
              type="number"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: 30 }}
              helperText="Anzahl der zusätzlichen Tage (1-30)"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Grund für Verlängerung"
              multiline
              rows={3}
              value={extendReason}
              onChange={(e) => setExtendReason(e.target.value)}
              placeholder="Grund für die weitere Verlängerung..."
            />

            <Alert severity="warning" sx={{ mt: 2 }}>
              Die Verlängerung wird ab heute für {extendDays} weitere Tag(e) gewährt und reaktiviert die Meldungsmöglichkeit.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialogOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleExtendExtension} 
            variant="contained"
            disabled={loading}
          >
            Verlängern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationExtensionsPanel;